// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { parseBoolean } from "@stoneage/XDL/Utilities";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

export interface JettingAppPageStyles {
    page?: MobiusPageProps;
    modal?: TwoButtonModalStyles;
}

const mapStateToProps = (state: CustomAppState) => ({
    location: getLocation(state),
});

export const jettingAppPageStyles: JettingAppPageStyles = {};

const styles = jettingAppPageStyles;

type Props = PageProps & HasHistory & ReturnType<typeof mapStateToProps>;

const JettingAppPage = ({ id, history, location }: Props) => {
    const [leaveJettingAppModalVisible, setLeaveJettingAppModalVisible] = useState(false);
    const [acceptingConfiguration, setAcceptingConfiguration] = useState(false);
    
    const isDone = useRef<string>("");

    // Leave page modal
    useEffect(() => {
        const _push = history.push;
        const _replace = history.replace;

        history.push = async (url, state: any) => {
            const res = await onChangeState(url, state?.passModal);
            if (res) {
                _push(url, state);
            }
        };
        history.replace = async (url, state: any) => {
            const res = await onChangeState(url, state?.passModal);
            if (res) {
                _replace(url, state);
            }
        };
        return () => {
            history.push = _push;
            history.replace = _replace;
        };
    }, []);

    const onChangeState = async (url: string, passModal: boolean) => {
        if (passModal) {
            return true;
        }

        isDone.current = "";
        if(parseBoolean(localStorage.getItem("jettingAppAcceptingConfiguration") || "false")) {
            setAcceptingConfiguration(true);
        }
        setLeaveJettingAppModalVisible(true);

        await waitFor(() => {
            return isDone.current !== "";
        });
        return isDone.current === "Accept";
    };

    const resetAcceptingConfiguration = () => {
        localStorage.removeItem("jettingAppAcceptingConfiguration");
        setAcceptingConfiguration(false);
    };

    return (
        <Page data-test-selector="jettingAppPage" {...styles.page}>
            <Zone contentId={id} zoneName="Content" />
            <TwoButtonModal
                headlineText={siteMessage("Custom_JettingAppLeaveWarningModal_Title")}
                messageText={acceptingConfiguration ? siteMessage("Custom_JettingAppLeaveAcceptingConfiguration_Message") : siteMessage("Custom_JettingAppLeaveWarningModal_Message")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Exit Page")}
                submitTestSelector="exitPage_Button"
                modalIsOpen={leaveJettingAppModalVisible}
                onCancel={() => {
                    isDone.current = "Reject";
                    setLeaveJettingAppModalVisible(false);
                    resetAcceptingConfiguration();
                }}
                onSubmit={() => {
                    isDone.current = "Accept";
                    setLeaveJettingAppModalVisible(false);
                    resetAcceptingConfiguration();
                }}
                extendedStyles={styles.modal}
            />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps)(withHistory(JettingAppPage)),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
    },
};

export default pageModule;

export const JettingAppPageContext = "JettingAppPage";
